/**
 * Who has Iris and whose conversation the widget keeps (CU-869fmq2bf). Pure — no React, no signals, no `window` —
 * so `node --test` runs it.
 *
 * Iris is the account's `iris` feature (`featuresV2.iris`), on by default and switched off per account with
 * `iris_off` in Admin > Users (webback `compute_features`): no login list on this side any more.
 */

/** The user (userData's value) has Iris: `featuresV2.iris`, nothing else. */
export function hasIris(user) {
	return Boolean(user?.featuresV2?.iris)
}

/*
 * The widget keeps its conversation in localStorage per agent, not per user (`dsb_sid_*`, `dsb_sessions_*`,
 * `dsb_hist_*`), and re-sends the stored history on every question. Another identity in the same browser must
 * never inherit it: the figures of account A would travel as context with the identity of B (TL, plan-review
 * CU-869fmq2bf obs. 7). We record whose conversation is stored and clear it when that changes.
 */
export const IRIS_IDENTITY_KEY = 'rentacar_iris_identity'
const WIDGET_PREFIX = 'dsb_'
// the drawer's width is a preference of the browser, not a conversation
const KEPT_WIDGET_KEYS = /^dsb_size/

/** Whose conversation it is: the logged-in uid and the account on screen (`uidSlave`); null logged out. */
export function irisIdentityKey(user) {
	if (user?.uid === undefined || user?.uid === null || user?.uid === '') return null
	return `${user.uid}|${user.uidSlave ?? ''}`
}

/** Remove the widget's conversation keys (`dsb_*`, not its drawer size); nothing else of the app. */
export function clearWidgetStorage(storage) {
	widgetKeys(storage).forEach(key => storage.removeItem(key))
}

function widgetKeys(storage) {
	const keys = []
	for (let i = 0; i < storage.length; i++) {
		const key = storage.key(i)
		if (key && key.startsWith(WIDGET_PREFIX) && !KEPT_WIDGET_KEYS.test(key)) keys.push(key)
	}
	return keys
}

/**
 * Before the widget loads (and when the user goes): another identity, or none, clears the stored conversation and
 * records the new identity. Returns whether it cleared. Never throws (storage blocked or missing).
 */
export function syncIrisStorage(storage, key) {
	try {
		if (!storage) return false
		const stored = storage.getItem(IRIS_IDENTITY_KEY)
		if (stored === key && (key !== null || widgetKeys(storage).length === 0)) return false
		clearWidgetStorage(storage)
		if (key === null) storage.removeItem(IRIS_IDENTITY_KEY)
		else storage.setItem(IRIS_IDENTITY_KEY, key)
		return true
	} catch {
		return false
	}
}

// Ids the widget and the app put on the page: the <script> the app injects (`PmsAgentWidget`) and the <style> the
// widget mounts in <head> (`widget.js` `mount()`, which also refuses to mount again while it is there)
export const WIDGET_SCRIPT_ID = 'rentacar-pms-agent-widget'
export const WIDGET_ROOT_ID = 'dsb-root'
// what `widget.js` `mount()` appends to <body>: the drawer and its own launcher (hidden here, `data-launcher="none"`)
const WIDGET_NODE_SELECTORS = ['.dsb-panel', '.dsb-btn']
// `data-push`: the drawer narrows the page with a margin and a class on <html>
const WIDGET_PUSHED_CLASS = 'dsb-pushed'

const safe = fn => { try { return fn() } catch { return undefined } }

/**
 * Remove every node of the widget from the page by hand — the script, the style, the panel, the launcher — and undo
 * the push margin on <html>. Returns how many nodes went. `doc` is `document`; never throws, nothing to do is fine.
 * The widget's own `destroy()` does the same for what IT created, but only once the script has mounted and only
 * if `window.DSB` is still the real handle; this is the fallback that does not depend on either.
 */
export function removeWidgetDom(doc) {
	if (!doc) return 0
	let removed = 0
	const drop = node => {
		if (node && node.parentNode) { node.parentNode.removeChild(node); removed += 1 }
	}
	drop(safe(() => doc.getElementById(WIDGET_SCRIPT_ID)))
	drop(safe(() => doc.getElementById(WIDGET_ROOT_ID)))
	WIDGET_NODE_SELECTORS.forEach(selector => {
		const nodes = safe(() => Array.from(doc.querySelectorAll(selector))) || []
		nodes.forEach(drop)
	})
	const html = doc.documentElement
	if (html) {
		safe(() => { html.style.marginRight = ''; html.style.marginLeft = '' })
		safe(() => html.classList.remove(WIDGET_PUSHED_CLASS))
	}
	return removed
}

/**
 * Take the loaded widget off the page: its panel, its push margin and its in-memory conversation (`DSB.destroy()`,
 * widget 0.120+), then whatever is still on the page by hand (`removeWidgetDom`). Removing the <script> is not
 * enough — the panel stays, and the next script refuses to mount while it is there — so on logout or another
 * identity user B would see A's conversation. A script still in flight at this moment has no handle yet (its
 * queue stub and `mount()` run in the same tick once it lands): it mounts an empty, closed panel with nothing to
 * show — the stored conversation is already gone — and the next mount's `syncIrisStorage` + `destroyWidget`
 * remove it. `win` is `window`. Returns whether destroy() ran; never throws; `win.DSB` is always null after.
 */
export function destroyWidget(win) {
	if (!win) return false
	const dsb = win.DSB
	let destroyed = false
	if (dsb) {
		try {
			if (typeof dsb.destroy === 'function') {
				dsb.destroy()
				destroyed = true
			}
		} catch {
			destroyed = false
		}
	}
	win.DSB = null
	removeWidgetDom(win.document)
	return destroyed
}

/**
 * What of the widget is still in the page or in storage — empty when nothing is. Each entry names one leftover:
 * `handle` (`window.DSB`), `script`, `style`, `panel`, `launcher`, `push` (the margin or class on <html>) and
 * `storage:<key>` per stored conversation key. `win` is `window`; never throws.
 */
export function widgetResidue(win) {
	if (!win) return []
	const found = []
	if (win.DSB) found.push('handle')
	const doc = win.document
	if (doc) {
		if (safe(() => doc.getElementById(WIDGET_SCRIPT_ID))) found.push('script')
		if (safe(() => doc.getElementById(WIDGET_ROOT_ID))) found.push('style')
		if ((safe(() => doc.querySelectorAll('.dsb-panel').length) || 0) > 0) found.push('panel')
		if ((safe(() => doc.querySelectorAll('.dsb-btn').length) || 0) > 0) found.push('launcher')
		const html = doc.documentElement
		const pushed = safe(() => Boolean(html.style.marginRight || html.style.marginLeft || html.classList.contains(WIDGET_PUSHED_CLASS)))
		if (pushed) found.push('push')
	}
	const storage = safe(() => win.localStorage)
	const keys = storage ? safe(() => widgetKeys(storage)) || [] : []
	keys.forEach(key => found.push(`storage:${key}`))
	return found
}

/**
 * Everything the app does when nobody is logged in any more: the widget goes (`destroyWidget`), its stored
 * conversation and the recorded identity go (`syncIrisStorage(…, null)`). Does not look at who is logged in —
 * the caller has decided. Returns `widgetResidue(win)`, expected empty. `win` is `window`; never throws.
 */
export function teardownIris(win) {
	if (!win) return []
	destroyWidget(win)
	syncIrisStorage(safe(() => win.localStorage) ?? null, null)
	return widgetResidue(win)
}
